import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useRouter } from 'next/router';
import _ from 'lodash';
import { Box, BoxProps } from '@design-system/mobile';
import styled from 'styled-components';
import classnames from 'classnames';
import { createPortal } from 'react-dom';
import { useWebApp } from '../WebAppProvider';

export interface Layer {
  id: string;
  visible: boolean;
}

const LayerPopUpContext = createContext({
  layers: [],
  attachLayer: () => {},
  detachLayer: () => {},
  isOpen: (_) => false,
  handleOpen: (_) => {},
  handleClose: (_) => {},
} as {
  layers: Layer[];
  attachLayer: (layer: Layer) => void;
  detachLayer: (id: string) => void;
  isOpen: (id: string) => boolean;
  handleOpen: (
    id: string,
    options?: {
      supportNativeBack?: boolean;
      ignoreNativeBack?: boolean;
      handleBeforeClose?: (handleClose: () => void) => Promise<void>;
    },
  ) => void;
  handleClose: (id: string) => void;
});

export const useLayerPopUp = () => {
  const context = useContext(LayerPopUpContext);

  if (context === undefined) {
    throw new Error('useLayerPopUp must be used within a LayerPopupContext');
  }

  return context;
};

export interface LayerPopUpProviderProps {
  children: ReactNode | ReactNode[];
}

const LayerPopUpProvider = ({ children }: LayerPopUpProviderProps) => {
  const [layers, setLayers] = useState<Layer[]>([]);

  const { handleFireNativeBack, detachCallback } = useWebApp();

  const attachLayer = useCallback((layer: Layer) => {
    setLayers((stack) => [layer, ...stack]);
  }, []);
  const detachLayer = useCallback((id: string) => {
    setLayers((stack) => {
      const newValues = [...stack];
      const index = _.findIndex(newValues, { id });
      if (index >= 0) {
        newValues.splice(index, 1);
      }
      return newValues;
    });
  }, []);

  const isOpen = useCallback(
    (id: string) => {
      const layer = _.find(layers, { id });
      return layer ? layer.visible : false;
    },
    [layers],
  );
  const handleClose = useCallback(
    (id: string) => {
      const newStack = [...layers];
      const index = _.findIndex(newStack, { id });
      if (index >= 0) {
        setLayers(() => {
          const newValues = [...newStack];
          newValues[index].visible = false;
          return newValues;
        });

        detachCallback(`FireNativeBack-${id}`);
      }
    },
    [detachCallback, layers],
  );
  const handleOpen = useCallback(
    (
      id: string,
      options?: {
        supportNativeBack?: boolean;
        ignoreNativeBack?: boolean;
        handleBeforeClose?: (handleClose: () => void) => Promise<void>;
      },
    ) => {
      const newStack = [...layers];
      const index = _.findIndex(newStack, { id });
      if (index >= 0) {
        setLayers(() => {
          const newValues = [...newStack];
          newValues[index].visible = true;
          return newValues;
        });

        if (options?.supportNativeBack) {
          handleFireNativeBack(`FireNativeBack-${id}`, async () => {
            if (!options?.ignoreNativeBack) {
              if (options?.handleBeforeClose) {
                options?.handleBeforeClose(() => handleClose(id));
              } else {
                handleClose(id);
              }
            }
          });
        }
      }
    },
    [handleClose, handleFireNativeBack, layers],
  );

  const lockScroll = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.document.body.style.overflowY = 'hidden';
    }
  }, []);
  const unlockScroll = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.document.body.style.overflowY = '';
    }
  }, []);
  useEffect(() => {
    const hasOpen = layers.reduce(
      (result, layer) => result || layer.visible,
      false,
    );
    if (hasOpen) {
      lockScroll();
    } else {
      unlockScroll();
    }
  });

  const router = useRouter();
  const closeAllWhenRouting = useCallback(() => {
    setLayers((stack) => {
      const newValues = stack.map((item) => {
        item.visible = false;
        return { ...item };
      });
      return [...newValues];
    });
  }, []);
  useEffect(() => {
    router.events.on('routeChangeComplete', closeAllWhenRouting);
    return () => {
      router.events.off('routeChangeComplete', closeAllWhenRouting);
    };
  }, [closeAllWhenRouting, router.events]);

  return (
    <LayerPopUpContext.Provider
      value={{
        layers,
        attachLayer,
        detachLayer,
        isOpen,
        handleOpen,
        handleClose,
      }}
    >
      {children}
      <LayerPopUpContext.Consumer>
        {(_) => <div id="layer-popup-portal"></div>}
      </LayerPopUpContext.Consumer>
    </LayerPopUpContext.Provider>
  );
};

export default LayerPopUpProvider;

export interface LayerPopUpProps extends Pick<Layer, 'id'> {
  depth?: 'MAX' | number;
  direction?: 'up' | 'down' | 'left' | 'right';
  children: (args: {
    isOpen: boolean;
    handleClose: (id: string) => void;
  }) => ReactNode | ReactNode[];
  forceUpdate?: boolean;
}

export const LayerPopUp = ({
  id,
  depth,
  direction,
  children,
  forceUpdate,
  ...otherProps
}: LayerPopUpProps & Omit<BoxProps, 'children'>) => {
  const [mounted, setMounted] = useState(false);

  const { layers, attachLayer, detachLayer, isOpen, handleClose } =
    useLayerPopUp();

  useEffect(() => {
    setMounted(true);
    attachLayer({
      id,
      visible: false,
    });
    return () => {
      setMounted(false);
      detachLayer(id);
    };
  }, [attachLayer, detachLayer, id]);

  const portalEl =
    typeof window !== 'undefined' &&
    document.querySelector('#layer-popup-portal');

  const zIndex = useMemo(() => {
    let value = depth;
    value = value || layers.length + 100;
    value = value === 'MAX' ? 999 : value;
    return value;
  }, [depth, layers.length]);

  return mounted && portalEl && children ? (
    createPortal(
      <LayerContainer
        id={id}
        zIndex={zIndex}
        className={classnames({
          open: isOpen(id),
          up: direction === 'up',
          down: direction === 'down',
          left: direction === 'left',
          right: direction === 'right',
          fix: !direction,
        })}
        {...(otherProps as unknown as any)}
      >
        {forceUpdate
          ? children({ handleClose, isOpen: isOpen(id) })
          : isOpen(id) && children({ handleClose, isOpen: isOpen(id) })}
      </LayerContainer>,
      portalEl,
    )
  ) : (
    <></>
  );
};

const LayerContainer = styled(Box)`
  position: fixed;
  overflow-x: hidden;
  overflow-y: auto;
  transition: all 0.3s ease-out;
  height: 100dvh;
  transform: scaleZ(0);
  &.open {
    transform: initial;
  }
  &.up {
    top: 100%;
    left: 0;
    right: 0;
    bottom: -100%;
  }
  &.down {
    top: -100%;
    left: 0;
    right: 0;
    bottom: 100%;
  }
  &.left {
    top: 0;
    left: 100%;
    right: -100%;
    bottom: 0;
  }
  &.right {
    top: 0;
    left: -100%;
    right: 100%;
    bottom: 0;
  }
  &.open {
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }
  &.fix {
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }
`;
